import React from 'react';
// import EventBusyOutlined from '@mui/icons-material/EventBusyOutlined';
import {BlockOutlined, CheckBoxOutlineBlank, RemoveShoppingCart} from '@mui/icons-material';
import { MeasurementUnits } from '../utils/enums';

type InventoryThumbSize = 'desktop' | 'mobile' | 'card';

const thumbSizeClasses: Record<InventoryThumbSize, string> = {
  desktop: 'w-24 h-24',
  mobile: 'w-20 h-20',
  card: 'w-[120px] min-h-[132px] h-full',
};

const thumbRadiusClasses: Record<InventoryThumbSize, string> = {
  desktop: 'rounded-lg',
  mobile: 'rounded-lg',
  card: 'rounded-l-xl',
};

type InventoryOutOfStockImageProps = {
  imageUrl?: string;
  itemName: string;
  size?: InventoryThumbSize;
};

export const InventoryOutOfStockImage: React.FC<InventoryOutOfStockImageProps> = ({
  imageUrl,
  itemName,
  size = 'desktop',
}) => {
  const thumbClass = thumbSizeClasses[size];
  const radiusClass = thumbRadiusClasses[size];

  return (
    <div className={`relative flex-shrink-0 ${thumbClass}`}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={itemName}
          className={`${thumbClass} ${radiusClass} object-cover grayscale brightness-95`}
        />
      ) : (
        <div className={`${thumbClass} ${radiusClass} bg-[#ECECEC]`} />
      )}
      <div className={`absolute inset-0 ${radiusClass} bg-white/55 backdrop-blur-[1px]`} />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-sm ring-1 ring-[#E9E9E9]">
          <BlockOutlined className="h-[18px] w-[18px] text-[#9CA3AF]" aria-hidden />
        </div>
      </div>
    </div>
  );
};

type InventoryOutOfStockBadgeProps = {
  size?: InventoryThumbSize;
};

export const InventoryOutOfStockBadge: React.FC<InventoryOutOfStockBadgeProps> = ({
  size = 'desktop',
}) => (
  <div
    className={`inline-flex items-center gap-1.5 self-start rounded-lg border-0 border-[#F0D0D0] bg-[#FDF5F5] font-raleway font-semibold text-[#B84C4C]/60 px-2 py-1 ${
      size === 'mobile' ? 'mt-2 px-2.5 py-1 text-[12px]' : 'mt-2 px-3 py-1 text-[13px]'
    }`}
  >
    <RemoveShoppingCart
      className={size === 'mobile' ? ' text-[#B84C4C]/60' : ' text-[#B84C4C]/60'}
      style={{ fontSize: size === 'mobile' ? '18px' : '20px' }}
      aria-hidden
    />
    <span>Currently not available</span>
  </div>
);

export const inventoryOutOfStockCardClassName =
  'border border-dashed border-[#E0E0E0] bg-[#FAFAFA] cursor-not-allowed select-none';

export const inventoryOutOfStockDescription =
  'All units are reserved for your selected session dates.';

export const getInventoryRemainingCount = (quantity: number, maxQty?: number) => {
  if (maxQty == null) return null;
  if (quantity > 0 && quantity <= maxQty) return maxQty - quantity;
  return maxQty;
};

const normalizeMeasureUnitKey = (measureUnit?: string) =>
  measureUnit?.trim().toLowerCase() ?? '';

const countMeasureUnitKeys = new Set(['ea', 'unit']);
const specialPluralMeasureUnitKeys = new Set(['lb', 'loaf', 'box']);
const pluralizableMeasureUnitKeys = new Set([
  'set',
  'pkg',
  'pack',
  'case',
  'roll',
  'sheet',
  'bag',
  'bottle',
  'can',
  'bunch',
  'slice',
  'head',
  'clove',
  'sprig',
  'stalk',
  'fillet',
  'serving',
  'portion',
  'stick',
  'cup',
  'day',
  'week',
  'month',
  'year',
]);

const isCountMeasureUnit = (measureUnit?: string) => {
  const normalized = normalizeMeasureUnitKey(measureUnit);
  if (!normalized || normalized === 'units') return true;

  return Object.entries(MeasurementUnits).some(
    ([key, value]) =>
      countMeasureUnitKeys.has(key) &&
      (key.toLowerCase() === normalized || value.toLowerCase() === normalized)
  );
};

const buildInvariantMeasureUnitsFromEnum = () => {
  const units = new Set<string>();

  for (const [key, value] of Object.entries(MeasurementUnits)) {
    if (
      countMeasureUnitKeys.has(key) ||
      specialPluralMeasureUnitKeys.has(key) ||
      pluralizableMeasureUnitKeys.has(key)
    ) {
      continue;
    }

    units.add(key.toLowerCase());
    units.add(value.toLowerCase());
  }

  units.add('in');
  units.add('lbs');
  units.add('pound');
  units.add('pounds');

  return units;
};

const invariantMeasureUnits = buildInvariantMeasureUnitsFromEnum();

const specialMeasureUnitPlurals: Record<string, { singular: string; plural: string }> = {
  lb: { singular: 'lb', plural: 'lbs' },
  lbs: { singular: 'lb', plural: 'lbs' },
  pound: { singular: 'lb', plural: 'lbs' },
  pounds: { singular: 'lb', plural: 'lbs' },
  [MeasurementUnits.lb.toLowerCase()]: { singular: 'lb', plural: 'lbs' },
  loaf: { singular: 'loaf', plural: 'loaves' },
  [MeasurementUnits.loaf.toLowerCase()]: { singular: 'loaf', plural: 'loaves' },
  box: { singular: 'box', plural: 'boxes' },
  [MeasurementUnits.box.toLowerCase()]: { singular: 'box', plural: 'boxes' },
};

const pluralizeMeasureUnitWord = (singular: string) => {
  if (singular.endsWith('s') || singular.endsWith('x') || singular.endsWith('z')) {
    return `${singular}es`;
  }
  if (singular.endsWith('ch') || singular.endsWith('sh')) {
    return `${singular}es`;
  }
  if (singular.endsWith('y') && !/[aeiou]y$/i.test(singular)) {
    return `${singular.slice(0, -1)}ies`;
  }
  if (singular.endsWith('f') && singular.length > 2) {
    return `${singular.slice(0, -1)}ves`;
  }
  return `${singular}s`;
};

const getMeasureUnitDisplayForm = (measureUnit?: string) => {
  const normalized = normalizeMeasureUnitKey(measureUnit);
  if (!normalized) return 'unit';

  const enumEntry = Object.entries(MeasurementUnits).find(
    ([key, value]) =>
      key.toLowerCase() === normalized || value.toLowerCase() === normalized
  );
  if (enumEntry) {
    return enumEntry[0];
  }

  return measureUnit?.trim() ?? 'unit';
};

export const formatInventoryMeasureUnitForLabel = (
  remaining: number,
  measureUnit?: string
) => {
  if (isCountMeasureUnit(measureUnit)) {
    return remaining === 1 ? 'unit' : 'units';
  }

  const normalized = normalizeMeasureUnitKey(measureUnit);
  const displayUnit = getMeasureUnitDisplayForm(measureUnit);
  const usePlural = remaining !== 1;

  if (!usePlural) {
    return specialMeasureUnitPlurals[normalized]?.singular ?? displayUnit;
  }

  if (specialMeasureUnitPlurals[normalized]) {
    return specialMeasureUnitPlurals[normalized].plural;
  }

  if (invariantMeasureUnits.has(normalized)) {
    return displayUnit;
  }

  return pluralizeMeasureUnitWord(displayUnit);
};

export const formatInventoryRemainingLabel = (
  quantity: number,
  maxQty?: number,
  measureUnit?: string
) => {
  const remaining = getInventoryRemainingCount(quantity, maxQty);
  if (remaining == null) return null;
  const unit = formatInventoryMeasureUnitForLabel(remaining, measureUnit);
  return `${remaining} ${unit} left`;
};

export const formatInventoryUnitPriceLabel = (
  unitPrice?: number,
  measureUnit?: string
) => {
  if (unitPrice == null || unitPrice <= 0) return null;
  return `$${unitPrice} / ${measureUnit ?? 'unit'}`;
};

export const InventoryOutOfStockCheckboxSlot: React.FC<{ positionClassName: string }> = ({
  positionClassName,
}) => (
  <div
    className={`${positionClassName} flex h-3.5 w-3.5 items-center justify-center rounded-none bg-[#ffffff] text-[#E9E9E9] ring-1 ring-[#E9E9E9]`}
    aria-hidden
  >
    <CheckBoxOutlineBlank className="h-3.5 w-3.5 text-[#E9E9E9]" />
  </div>
);
